import decompress from 'brotli/decompress';

// Bodies come back from GET /v2/bodies/{ref} exactly as they were stored:
// `brotli_<base64>`. Stripping that wrapper can leave ANOTHER encoding inside —
// most commonly the original HTTP-wire `Content-Encoding: gzip` body captured
// verbatim from the airline. UTF-8 decoding gzipped bytes produces a flood of
// U+FFFD replacement characters (every byte >= 0x80 is invalid UTF-8) and shows
// up as garbled text; that was a real reported bug on sandbox for gzipped
// AirShopping responses.
//
// Ported from tracer-frontend, where this behaviour was learned the hard way.
// Handles both the storage wrapper and the inner content-encoding so callers
// never have to think about it.

const STORAGE_BROTLI_PREFIX = 'brotli_';
const GZIP_MAGIC = [0x1f, 0x8b];

/**
 * Decode a stored body into displayable text.
 *
 * Accepts the `brotli_<base64>` wrapper the tracer writes, and falls back to
 * treating the input as a literal string for anything else.
 */
export async function decodeMessageBody(stored: string): Promise<string> {
  if (!stored) return '';

  let payload: Uint8Array;
  if (stored.startsWith(STORAGE_BROTLI_PREFIX)) {
    const compressed = stored.slice(STORAGE_BROTLI_PREFIX.length);
    payload = decompress(base64ToBytes(compressed));
  } else {
    payload = new TextEncoder().encode(stored);
  }

  if (isGzipped(payload)) {
    const ds = new DecompressionStream('gzip');
    const ungzipped = await new Response(
      new Blob([payload as BlobPart]).stream().pipeThrough(ds),
    ).arrayBuffer();
    return new TextDecoder('utf-8').decode(ungzipped);
  }

  return new TextDecoder('utf-8').decode(payload);
}

/**
 * Browser-native base64 decode.
 *
 * The old frontend used Node's `Buffer` here, which meant shipping a Buffer
 * polyfill to the browser purely for this. `atob` is built in.
 */
function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function isGzipped(buf: Uint8Array): boolean {
  return buf.length >= 2 && buf[0] === GZIP_MAGIC[0] && buf[1] === GZIP_MAGIC[1];
}
